import { useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { useModalBackClose } from "../../hooks/useModalBackClose";

/**
 * The dialog primitive.
 *
 * Stays a centred dialog at every size — never becomes a bottom sheet on mobile.
 * The mobile affordance is the hardware Back button instead, which is what
 * people actually reach for.
 *
 * Unmounts on close, so there is no exit animation by design: an exit animation
 * would require keeping a closed dialog mounted, and a dismissed dialog lingering
 * in the tree is a reliable source of stale-state bugs.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  maxWidth = "max-w-sm",
  closeLabel = "Close",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  maxWidth?: string;
  closeLabel?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useModalBackClose(open, onClose);

  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Moves focus into the dialog on open. Without this, focus stays on the button
  // that opened it, so a screen reader keeps reading the page behind the dialog
  // and Tab walks the background instead of the dialog's own controls.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/55 p-4 animate-fade-in backdrop-blur-sm"
      role="presentation"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className={`max-h-[calc(100dvh-2rem)] w-full overflow-y-auto ${maxWidth} animate-scale-in rounded-2xl border border-line/60 bg-card p-5 shadow-lg outline-none sm:p-6`}
      >
        <div className="mb-5 flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold tracking-tight text-ink-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            // Negative margin pulls the 40px hit area flush with the panel edge
            // while keeping the target big enough for a thumb.
            className="-m-1 flex size-10 items-center justify-center rounded-lg text-muted-subtle transition-colors hover:bg-ink-100 hover:text-ink-700"
          >
            <X className="size-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
